import { hasAnyScope } from '../permissions/scopePermissions';

/**
 * APP_NAV_ITEMS — single source of truth for navigation AND route generation.
 *
 * Drives:
 *   1. Sidebar (grouped + scope-filtered via getAccessibleNavGroups)
 *   2. React Router dynamic routes in routes/index.jsx (flat APP_NAV_ITEMS)
 *   3. First-accessible-route redirect (getAccessibleNavItems)
 *
 * Grouping: set `group` (+ optional `groupIconKey`) on items that belong
 * under a collapsible parent menu (e.g. "System Administrator"). Items with
 * no `group` render as top-level links. Order within this array is preserved
 * in the sidebar; the first item of a group defines the group's position.
 *
 * When adding a module:
 *   1. Add entry here (with `group` if it belongs under a parent)
 *   2. Add scope aliases in src/permissions/scopePermissions.js
 *   3. Add renderModuleRoute() case in routes/index.jsx
 */
export const APP_NAV_ITEMS = [
  {
    path:           '/dashboard',
    label:          'Dashboard',
    iconKey:        'dashboard',
    eyebrow:        'MedTransact Dashboard',
    description:    'High-level summary and key metrics.',
    requiredScopes: ['dashboard.view'],
  },

  // ── MedTransact: Medical reference data ─────────────────────────────────
  {
    path:           '/medical/provider',
    label:          'Provider',
    iconKey:        'provider',
    eyebrow:        'Medical',
    description:    'Healthcare providers (clinics / hospitals), accreditation, banking + contact details.',
    requiredScopes: ['providers.view'],
    group:          'Medical',
    groupIconKey:   'medical',
  },
  {
    path:           '/medical/diagnosis',
    label:          'Diagnosis',
    iconKey:        'diagnosis',
    eyebrow:        'Medical',
    description:    'ICD-10 diagnosis codes and categories.',
    requiredScopes: ['diagnoses.view'],
    group:          'Medical',
    groupIconKey:   'medical',
  },
  {
    path:           '/medical/cpt',
    label:          'CPT',
    iconKey:        'cpt',
    eyebrow:        'Medical',
    description:    'CPT procedure codes and default amounts.',
    requiredScopes: ['cpts.view'],
    group:          'Medical',
    groupIconKey:   'medical',
  },
  {
    path:           '/medical/physician',
    label:          'Physician',
    iconKey:        'physician',
    eyebrow:        'Medical',
    description:    'Accredited physicians and acceptance status.',
    requiredScopes: ['physicians.view'],
    group:          'Medical',
    groupIconKey:   'medical',
  },

  // ── MedTransact: Corporate ──────────────────────────────────────────────
  {
    path:           '/corporate/accounts',
    label:          'Corporate',
    iconKey:        'corporate',
    eyebrow:        'Corporate',
    description:    'Corporate clients, plan limits, and effectivity dates.',
    requiredScopes: ['corporates.view'],
    group:          'Corporate',
    groupIconKey:   'corporate',
  },
  {
    path:           '/corporate/plan-setup',
    label:          'Plan Setup',
    iconKey:        'planSetup',
    eyebrow:        'Corporate',
    description:    'Products, plan benefit limits, and coverage configuration.',
    requiredScopes: ['plan-setups.view'],
    group:          'Corporate',
    groupIconKey:   'corporate',
  },

  // ── MedTransact: Member / Payments / MIS ────────────────────────────────
  {
    path:           '/member',
    label:          'Member',
    iconKey:        'member',
    eyebrow:        'Enrollment',
    description:    'Member enrollment, eligibility, and LOA transactions.',
    requiredScopes: ['members.view'],
  },
  {
    path:           '/payments',
    label:          'Payments',
    iconKey:        'payments',
    eyebrow:        'Settlement',
    description:    'Transaction settlement — mark LOAs paid with bank + reference details.',
    requiredScopes: ['payments.view'],
  },
  {
    path:           '/mis-report',
    label:          'MIS Report',
    iconKey:        'misReport',
    eyebrow:        'Reporting',
    description:    'Management information reports across members, transactions, and providers.',
    requiredScopes: ['mis-reports.view'],
  },

  {
    path:           '/admin/users',
    label:          'OAuth Users',
    iconKey:        'oauthUsers',
    eyebrow:        'Admin',
    description:    'Create, edit, and assign roles + scopes to OAuth users.',
    requiredScopes: ['oauth-users.view'],
    group:          'System Administrator',
    groupIconKey:   'admin',
  },
  {
    path:           '/admin/oauth-clients',
    label:          'OAuth Clients',
    iconKey:        'oauthClients',
    eyebrow:        'Admin',
    description:    'Register, rotate secrets, and manage OAuth client credentials.',
    requiredScopes: ['oauth-clients.view'],
    group:          'System Administrator',
    groupIconKey:   'admin',
  },
  {
    path:           '/admin/upload-s3-public-share-tokens',
    label:          'Public Share Tokens',
    iconKey:        'shareToken',
    eyebrow:        'Admin',
    description:    'Permanent public share URLs for S3-uploaded files. Manage, regenerate, and revoke.',
    requiredScopes: ['upload-s3-public-share-token.view'],
    group:          'System Administrator',
    groupIconKey:   'admin',
  },
  // ── Disabled for MedTransact (scaffold template/social modules) ─────────
  // Code kept intact; simply not surfaced in the MedTransact sidebar.
  /*
  {
    path:           '/example-batch-upload',
    label:          'Example Batch Upload',
    iconKey:        'batchUpload',
    eyebrow:        'Template',
    description:    'Reference CSV/XLSX import. Daredaretyo per-row processing — copy this module for your own batch imports.',
    requiredScopes: ['example-batch-upload.view'],
    group:          'Tools',
    groupIconKey:   'tools',
  },
  // Architecture Rule 75 — Bloom Stranger sits ABOVE Chat in the sidebar.
  // Bloom is the discovery entry-point; Chat is what unlocks AFTER a mutual
  // Add Friend tap. The eyebrow carries the brand tagline literally so it
  // shows on hover/eyebrow without bloating the sidebar label.
  {
    path:           '/bloom-stranger',
    label:          'Bloom Stranger',
    iconKey:        'bloomStranger',
    eyebrow:        'Meet strangers. Bloom into friends.',
    description:    'Random anonymous video matchmaking. Tap Start, meet someone new, and if you both tap Add Friend the bloom unlocks a private chat + voice/video DM.',
    requiredScopes: ['bloom-stranger.view'],
  },
  {
    path:           '/chat',
    label:          'Bloom Chat',
    iconKey:        'chat',
    eyebrow:        'Realtime',
    description:    '1-to-1 and group conversations with live message delivery via WebSocket. Direct chats also unlock after a mutual Bloom (Rule 75). Includes voice + video calls (Rule 74) accessible from the thread header.',
    requiredScopes: ['chat.view'],
  },
  // Architecture Rule 76 — My Bloom sits AFTER Chat and Bloom Stranger in
  // the sidebar. The receiver-side wallet ↔ sender-side gift flow forms a
  // closed loop: Stranger → Chat → Send-Bloom → My-Bloom (where it lands).
  {
    path:           '/my-bloom',
    label:          'My Bloom',
    iconKey:        'bloomWallet',
    eyebrow:        'Your bloom wallet',
    description:    'Wallet balance, received-gift history, and withdrawal requests. Everything that landed in your bloom — and the way out — lives here.',
    requiredScopes: ['bloom-wallet.view'],
  },
  // Architecture Rule 74 — Voice + Video Calls deliberately have NO
  // top-level nav entry. The in-call page (/call/:callId) is reached by
  // starting/accepting a call; per-thread history (/threads/:threadId/calls)
  // is reached from the chat thread header. Both are registered as standalone
  // routes in src/routes/index.jsx, not iterated from this nav list.

  {
    path:           '/admin/bloom-withdrawals',
    label:          'Bloom Withdrawals',
    iconKey:        'bloomWallet',
    eyebrow:        'Admin',
    description:    'Phase-1 manual payout queue (Rule 76). Review pending withdrawal requests, mark them processing while you send the GCash/Maya/bank transfer, and mark completed with the external reference code.',
    requiredScopes: ['bloom-wallet.manage'],
    group:          'System Administrator',
    groupIconKey:   'admin',
  },
  */
  {
    path:           '/admin/audit-logs',
    label:          'Audit Logs',
    iconKey:        'auditLogs',
    eyebrow:        'Admin',
    description:    'System-wide activity trail — who did what, when, on which resource. Read-only.',
    requiredScopes: ['admin-audit-logs.view'],
    group:          'System Administrator',
    groupIconKey:   'admin',
  },
  {
    path:           '/admin/outbound-http-logs',
    label:          'Outbound HTTP Logs',
    iconKey:        'outboundHttp',
    eyebrow:        'Admin',
    description:    'Every outbound HTTP call the app made — request/response samples, latency. Read-only.',
    requiredScopes: ['admin-outbound-http-logs.view'],
    group:          'System Administrator',
    groupIconKey:   'admin',
  },
  {
    path:           '/admin/field-history',
    label:          'Field History',
    iconKey:        'fieldHistory',
    eyebrow:        'Admin',
    description:    'Per-column change timeline across every table — old → new value. Read-only.',
    requiredScopes: ['admin-field-history.view'],
    group:          'System Administrator',
    groupIconKey:   'admin',
  },
  {
    path:           '/admin/login-attempts',
    label:          'Login Attempts',
    iconKey:        'loginAttempts',
    eyebrow:        'Admin',
    description:    'Every authentication attempt and lockout signal. Read-only.',
    requiredScopes: ['admin-login-attempts.view'],
    group:          'System Administrator',
    groupIconKey:   'admin',
  },
  /* Disabled for MedTransact (chat module not used):
  {
    path:           '/admin/chat-messages',
    label:          'Chat Messages',
    iconKey:        'chatMessages',
    eyebrow:        'Admin',
    description:    'All chat messages across every thread, including deleted, for moderation. Read-only.',
    requiredScopes: ['admin-chat-messages.view'],
    group:          'System Administrator',
    groupIconKey:   'admin',
  },
  */
  {
    path:           '/admin/webhook-deliveries',
    label:          'Webhook Deliveries',
    iconKey:        'webhookDeliveries',
    eyebrow:        'Admin',
    description:    'Every outbound webhook delivery attempt across all subscriptions. Read-only.',
    requiredScopes: ['admin-webhook-deliveries.view'],
    group:          'System Administrator',
    groupIconKey:   'admin',
  },
  {
    path:           '/admin/failed-jobs',
    label:          'Failed Jobs',
    iconKey:        'failedJobs',
    eyebrow:        'Admin',
    description:    'Dead-letter queue — jobs that exhausted retries (Rule 32). Re-queue with one click.',
    requiredScopes: ['admin-failed-jobs.view'],
    group:          'System Administrator',
    groupIconKey:   'admin',
  },
  {
    path:           '/admin/system-settings',
    label:          'System Settings',
    iconKey:        'tools',
    eyebrow:        'Admin',
    description:    'Runtime-tunable knobs (Rule 74 Phase 1). Edit max group call participants, ringing TTL, call token TTL, and more — changes apply within milliseconds. Direct-call cap stays immutable.',
    requiredScopes: ['system-settings.view'],
    group:          'System Administrator',
    groupIconKey:   'admin',
  },

  // Add new module nav items here. Set `group` + `groupIconKey` to nest it
  // under a collapsible parent (reuse an existing group label to extend it):
  // {
  //   path: '/{route-path}', label: '{Module Label}', iconKey: '{camelCaseIconKey}',
  //   eyebrow: '{Module Label}', description: '{One sentence.}',
  //   requiredScopes: ['{module-key}.view'],
  //   group: 'System Administrator', groupIconKey: 'admin',
  // },
];

/**
 * True if the user holds any scope required by this nav item.
 * Special case: /dashboard is accessible with zero scopes.
 */
export function canAccessNavItem(userScopes, navItem) {
  const normalized = Array.isArray(userScopes) ? userScopes : [];
  if (navItem?.path === '/dashboard' && normalized.length === 0) return true;
  return hasAnyScope(normalized, navItem?.requiredScopes ?? []);
}

/**
 * Flat list of accessible items (used for the fallback-route redirect).
 * Falls back to dashboard if nothing matches.
 */
export function getAccessibleNavItems(userScopes) {
  const items = APP_NAV_ITEMS.filter((item) => canAccessNavItem(userScopes, item));
  return items.length > 0 ? items : APP_NAV_ITEMS.filter((i) => i.path === '/dashboard');
}

/**
 * Sidebar structure: an ordered list of entries that are either
 *   { type: 'item',  item }
 *   { type: 'group', label, iconKey, children: [item, ...] }
 *
 * Groups appear at the position of their first accessible child. A group
 * with no accessible children is omitted entirely.
 */
export function getAccessibleNavGroups(userScopes) {
  const accessible = getAccessibleNavItems(userScopes);
  const entries = [];
  const groupIndex = new Map();

  accessible.forEach((item) => {
    if (!item.group) {
      entries.push({ type: 'item', item });
      return;
    }
    if (!groupIndex.has(item.group)) {
      const group = {
        type:     'group',
        label:    item.group,
        iconKey:  item.groupIconKey ?? 'folder',
        children: [],
      };
      groupIndex.set(item.group, group);
      entries.push(group);
    }
    groupIndex.get(item.group).children.push(item);
  });

  return entries;
}
